import {combineReducers, configureStore} from '@reduxjs/toolkit';
import envData from 'env.json';
import {Platform, Settings} from 'react-native';
import {
  DEFAULT_VERSION,
  PersistedState,
  persistReducer,
  persistStore,
} from 'redux-persist';
import createSagaMiddleware from 'redux-saga';

import {AffiliateState} from './affiliate/affiliate.reducer';
import {ContentState} from './content';
import {MarketsState} from './markets/markets.reducer';
import {OffersState} from './offers/offers.reducer';
import {ProfileState} from './profile/profile.reducer';
import {PropertiesState} from './properties';
import reducers from './reducers';
import {ReservationState} from './reservations/reservations.reducer';
import sagas from './sagas';
import storage from './storage';
import {TargetState} from './target/target.reducer';
import {UIState} from './ui/ui.reducer';
import {PaymentsState} from './payments/payments.reducer';
import {AuthState} from './auth/auth.reducer';

const {API_URL, USE_STORYBOOK, REDUX_DEVTOOLS} = envData;
export interface AppState {
  readonly content: ContentState;
  readonly ui: UIState;
  readonly markets: MarketsState;
  readonly offers: OffersState;
  readonly properties: PropertiesState;
  readonly reservations: ReservationState;
  readonly target: TargetState;
  readonly profile: ProfileState;
  readonly auth: AuthState;
  readonly affiliate: AffiliateState;
  readonly payments: PaymentsState;
}

const sagaMiddleware = createSagaMiddleware({});

const middleware = [sagaMiddleware];

export const rootReducer = reducers;

/**
 * Redux persist version number (track/clear/migrate the redux persist store),
 * on version change.
 *
 * This should be bumped any time existing store changes are not passive.
 */
const CURRENT_PERSIST_VERSION = 30;

/**
 * Helper for clearing out persisted state during migration.
 * Modified version of redux-persist/createMigrate, removed the
 * requirement for a MigrationManifest. This frees up the dev from
 * having to create a new MigrationManifest every time the version on redux
 * is incremented. This is helpful considering for every new version of redux
 * all we want to do is clear out the persisted state.
 */
const clearPersistedState = (
  state: PersistedState,
  currentVersion: number
): Promise<PersistedState> => {
  if (!state) {
    return Promise.resolve(state);
  }

  const inboundVersion = state?._persist?.version ?? DEFAULT_VERSION;

  if (inboundVersion >= currentVersion) {
    return Promise.resolve(state);
  }

  return new Promise((resolve, reject) => {
    try {
      const migratedState = Object.keys(rootReducer).reduce(
        (acc, cur) => (acc[cur] = {} as any),
        {}
      ) as any;

      resolve(migratedState);
    } catch (err) {
      reject(err);
    }
  });
};

const blacklist = ['ui'];

const persistConfig = {
  key: 'myrewards',
  version: CURRENT_PERSIST_VERSION,
  storage,
  blacklist,
  migrate: clearPersistedState,
};

const persistedReducer = persistReducer(
  persistConfig,
  combineReducers(rootReducer) as any
);

export const store = configureStore({
  reducer: persistedReducer,
  middleware,
  devTools: REDUX_DEVTOOLS,
});

export const persistor = persistStore(store);

try {
  // Settings are always removed from device when app is removed. Persistor lives in keychain on iOS and can outlive the app
  if (Platform.OS === 'ios' && Settings.get('launchedBefore') === undefined) {
    persistor.purge();
    Settings.set({launchedBefore: true});
  }
} catch (e) {}

sagaMiddleware.run(sagas);
